import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, MessageCircle, Phone, Check } from "lucide-react";
import { SERVICES, IMAGES, CONTACT, WHATSAPP_URL, TEL_URL } from "../data/site";
import { useSEO } from "../lib/seo";

export default function ServiceDetail() {
    const { slug } = useParams();
    const service = SERVICES.find((s) => s.slug === slug);
    const others = SERVICES.filter(
        (s) => s.slug !== slug && !s.hiddenFromGrid
    ).slice(0, 3);

    useSEO({
        title: service
            ? `${service.seoTitle} | Cegu Masaj Salonu Özlüce - Bursa`
            : "Cegu Masaj",
        description: service ? service.long.slice(0, 160) : "",
        keywords: service ? service.keywords.join(", ") : "",
        canonical: `/hizmet/${slug}`,
    });

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [slug]);

    if (!service) return <Navigate to="/" replace />;

    return (
        <main
            data-testid={`service-page-${service.slug}`}
            className="bg-[#0d0d0f] pt-[104px] pb-16"
        >
            <div className="container-x">
                <Link
                    to="/#hizmetler"
                    className="inline-flex items-center gap-2 text-[13px] text-[#c8c3ba] hover:text-[#c9a24b] mb-6"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Tüm Hizmetler
                </Link>

                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
                    <div className="lg:col-span-6">
                        <div className="hero-frame aspect-[4/3]">
                            <img
                                src={IMAGES[service.image]}
                                alt={`${service.seoTitle} - Cegu Masaj`}
                                className="w-full h-full object-cover"
                                loading="eager"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <div className="chip mb-4">
                            <Clock className="w-3 h-3" />
                            {service.duration}
                        </div>
                        <h1 className="h1 text-[#f2ede4]">
                            {service.seoTitle}
                        </h1>
                        <div className="divider-gold mt-5" />

                        <p className="mt-6 body-base">{service.long}</p>
                        <p className="mt-4 body-base">
                            Cegu Masaj Salonu Özlüce'de{" "}
                            {service.title.toLowerCase()} seansları uzman
                            terapistler tarafından, hijyenik ortamda ve size özel
                            tempolarda uygulanır. Bursa Nilüfer'in kalbinde, Batı
                            Palas içerisindeyiz.
                        </p>

                        <ul className="mt-6 grid grid-cols-2 gap-y-2 gap-x-4 text-[14px] text-[#c8c3ba]">
                            {["Uzman terapist", "Hijyenik ortam", "Konforlu oda", "Hızlı randevu"].map(
                                (t) => (
                                    <li
                                        key={t}
                                        className="inline-flex items-center gap-2"
                                    >
                                        <Check className="w-4 h-4 text-[#c9a24b] shrink-0" />
                                        {t}
                                    </li>
                                )
                            )}
                        </ul>

                        <div className="mt-8 flex flex-wrap gap-3">
                            <a
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="service-cta-whatsapp"
                                className="btn btn-gold"
                            >
                                <MessageCircle className="w-4 h-4" />
                                WhatsApp'tan Randevu Al
                            </a>
                            <a href={TEL_URL} className="btn btn-ghost">
                                <Phone className="w-4 h-4" />
                                {CONTACT.phone}
                            </a>
                        </div>

                        <div className="mt-8 pt-6 border-t border-[#26262c]">
                            <div className="text-[11px] uppercase tracking-[0.22em] text-[#c9a24b] mb-3">
                                Anahtar Sözcükler
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {service.keywords.map((k) => (
                                    <span
                                        key={k}
                                        className="text-[12px] px-3 py-1.5 rounded-full border border-[#26262c] text-[#c8c3ba]"
                                    >
                                        {k}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Others */}
                <div className="mt-20">
                    <h2 className="h3 text-[#f2ede4] mb-6">
                        Diğer Hizmetlerimiz
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {others.map((o) => (
                            <Link
                                key={o.slug}
                                to={`/hizmet/${o.slug}`}
                                className="card"
                            >
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    <img
                                        src={IMAGES[o.image]}
                                        alt={o.seoTitle}
                                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="p-5">
                                    <div className="text-[11px] uppercase tracking-widest text-[#c9a24b]">
                                        {o.duration}
                                    </div>
                                    <div className="mt-1 text-[16px] font-semibold text-[#f2ede4]">
                                        {o.title}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </main>
    );
}
